import { MegaphoneIcon, Share2Icon, SparklesIcon, TagIcon, type LucideIcon } from 'lucide-react';

import { AnalysisSubHeader, IconBadgeCard } from '../../../../components';

import { StatistaChartCard } from '../StatistaCharts';

import type { EstablishedMarketNormsProps } from './EstablishedMarketNorms.types';
import type { EstablishedNormsDto } from '@inference.engine/api-client';
import { useTranslation } from 'react-i18next';

import { hasContent } from '../../../../utils/hasContent';

interface NormCard {
  key: keyof EstablishedNormsDto;
  labelKey: string;
  icon: LucideIcon;
}

const NORM_CARDS = [
  {
    key: 'pricing',
    labelKey: 'analysisResult.establishedMarketNorms.pricing',
    icon: TagIcon,
  },
  {
    key: 'distribution',
    labelKey: 'analysisResult.establishedMarketNorms.distribution',
    icon: Share2Icon,
  },
  {
    key: 'communication',
    labelKey: 'analysisResult.establishedMarketNorms.communication',
    icon: MegaphoneIcon,
  },
  {
    key: 'innovation',
    labelKey: 'analysisResult.establishedMarketNorms.innovation',
    icon: SparklesIcon,
  },
] as const satisfies readonly NormCard[];

export const hasEstablishedNorms = (establishedNorms: EstablishedNormsDto | undefined) =>
  NORM_CARDS.some((card) => hasContent(establishedNorms?.[card.key]));

export const EstablishedMarketNorms = ({
  establishedNorms,
  citedDatasets,
}: EstablishedMarketNormsProps) => {
  const { t } = useTranslation('analysis');

  const cards = NORM_CARDS.filter((card) => hasContent(establishedNorms?.[card.key]));
  if (cards.length === 0) return null;

  return (
    <div className="flex flex-col gap-6">
      <AnalysisSubHeader
        title={t('analysisResult.establishedMarketNorms.establishedMarketNorms')}
        subtitle={t('analysisResult.establishedMarketNorms.categoryConventions')}
        subtitleTone="meta"
      />
      <div className="grid grid-cols-1 items-stretch gap-sm md:grid-cols-2">
        {cards.map((card) => (
          <IconBadgeCard
            key={card.key}
            icon={card.icon}
            title={t(card.labelKey)}
            description={establishedNorms?.[card.key]}
          />
        ))}
      </div>
      {citedDatasets?.map((cited) => (
        <StatistaChartCard key={cited.id} dataset={cited} citedIn={establishedNorms} />
      ))}
    </div>
  );
};
